import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { useNotifications } from "../hooks/useNotifications";
import { useLogistics } from "../hooks/useLogistics";
import { usePOs } from "../hooks/usePOs";
import NotificationBell from "./ui/NotificationBell";
import {
  LayoutDashboard,
  Users,
  FileSpreadsheet,
  FileQuestion,
  Truck,
  Package,
  LogOut,
  Menu,
  X,
  Shield,
  Settings,
  UserCog,
  User,
  Handshake,
  PieChart,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Bug,
  History,
  MessageSquare,
  CheckSquare
} from "lucide-react";
import SendToDeveloperModal from "./modals/SendToDeveloperModal";
import WeeklyStatusLogsModal from "./modals/WeeklyStatusLogsModal";

export default function Navbar() {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "true");
  const sidebarRef = React.useRef(null);
  const [profilePic, setProfilePic] = useState("");
  const [userName, setUserName] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [openMenus, setOpenMenus] = useState({});
  const [userRole, setUserRole] = useState("");
  const [userRoles, setUserRoles] = useState([]);
  const [developerModalOpen, setDeveloperModalOpen] = useState(false);
  const [weeklyStatusLogsModalOpen, setWeeklyStatusLogsModalOpen] = useState(false);
  const { shipments } = useLogistics();
  const { pos } = usePOs();
  const { notifications, markAsRead, markAllAsRead, clearAll, generateDelayedShippingNotifications, generateOutstandingPoNotifications, getVisibleNotifications, getUnreadCount } = useNotifications(currentUser);

  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add("sidebar-collapsed");
    } else {
      document.body.classList.remove("sidebar-collapsed");
    }
  }, [isCollapsed]);

  // Collapse submenus and close mobile nav on navigation
  useEffect(() => {
    if (isCollapsed) {
      setOpenMenus({});
    }
    setIsOpen(false);
  }, [location.pathname, isCollapsed]);

  // Lock body scrolling when mobile drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("mobile-nav-open");
    } else {
      document.body.classList.remove("mobile-nav-open");
    }
    return () => {
      document.body.classList.remove("mobile-nav-open");
    };
  }, [isOpen]);

  // Collapse submenus when clicking outside the sidebar when collapsed
  useEffect(() => {
    if (!isCollapsed) return;
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setOpenMenus({});
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem("sidebar_collapsed", String(newValue));
      return newValue;
    });
  };

  const toggleMenu = (name) => {
    setOpenMenus(prev => ({ ...prev, [name]: !prev[name] }));
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "company_logos"), (snapshot) => {
      let activeUrl = "";
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.isActive) {
          activeUrl = data.url;
        }
      });
      setCompanyLogo(activeUrl);
    }, (error) => {
      console.error("Error subscribing to company logos in Navbar:", error);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser || !currentUser.email) return;

    const q = query(collection(db, "users"), where("email", "==", currentUser.email));
    const capitalizeName = (name) => {
      if (!name) return "";
      return name.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    };

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        if (data.photoURL) setProfilePic(data.photoURL);
        if (data.name) {
          setUserName(capitalizeName(data.name));
        } else {
          setUserName(capitalizeName(currentUser.email.split("@")[0]));
        }
        setUserRole(data.role || "");
        setUserRoles(data.roles || []);
      } else {
        setUserName(capitalizeName(currentUser.email.split("@")[0]));
        setUserRoles([]);
      }
    }, (err) => {
      console.error("Error loading user profile in navbar:", err);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Generate delayed shipping notifications on data change
  useEffect(() => {
    if (shipments.length > 0 && pos.length > 0) {
      generateDelayedShippingNotifications(shipments, pos);
    }
  }, [shipments, pos]);

  // Generate outstanding open PO notifications on data change
  useEffect(() => {
    if (pos.length > 0) {
      generateOutstandingPoNotifications(pos);
    }
  }, [pos, generateOutstandingPoNotifications]);

  const [hasUnreadChat, setHasUnreadChat] = useState(false);

  useEffect(() => {
    if (location.pathname === "/chat") {
      setHasUnreadChat(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!currentUser || location.pathname === "/chat") {
      setHasUnreadChat(false);
      return;
    }

    const q = collection(db, "chats");
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let unread = false;
      const myEmail = currentUser.email?.toLowerCase();
      const myUid = currentUser.uid;

      snapshot.forEach((doc) => {
        const chat = doc.data();
        
        // Only verify membership for DMs
        if (chat.type === "dm") {
          const isMember = chat.members && chat.members.includes(myEmail);
          if (!isMember) return;
        }

        // If there is an uninvited members limit, we can check it
        const invitedMembers = chat.invitedMembers || [];
        if (chat.type === "channel" && invitedMembers.length > 0) {
          const isInvited = invitedMembers.includes(myEmail);
          if (!isInvited) return;
        }

        // Check if there is a last message and it's not sent by current user
        if (chat.lastMessageTimestamp && chat.lastMessageSenderId !== myUid) {
          const lastReadTime = chat.lastRead?.[myUid] || "";
          if (!lastReadTime || chat.lastMessageTimestamp > lastReadTime) {
            unread = true;
          }
        }
      });

      setHasUnreadChat(unread);
    }, (err) => {
      console.error("Error subscribing to unread chats in navbar:", err);
    });

    return () => unsubscribe();
  }, [currentUser, location.pathname]);

  // Role Access Checks
  const canAccessBD = userRoles.includes("Business Development") || userRoles.includes("Admin");
  const canAccessProcurement = userRoles.some(r => ["Admin", "Manager", "Procurement", "Logistics", "Warehousing", "Sales", "Developer"].includes(r));
  const canAccessCRM = userRoles.some(r => ["Admin", "Manager", "Procurement", "Sales"].includes(r));
  const canAccessManagement = userRoles.some(r => ["Admin", "Manager", "Procurement", "Sales", "Developer"].includes(r));
  const canAccessSettings = userRoles.some(r => ["Admin", "Manager", "Procurement", "Sales", "Developer"].includes(r));
  const canAccessAdminTools = userRoles.includes("Admin");

  const navItems = [
    { name: "Dashboard", path: "/", icon: LayoutDashboard },
    { name: "Chat", path: "/chat", icon: MessageSquare },
    { name: "Tasks", path: "/tasks", icon: CheckSquare },
    ...(canAccessProcurement
      ? [{
          name: "Procurement",
          icon: Package,
          subItems: [
            { name: "Procurement", path: "/po" },
            { name: "Purchase", path: "/procurement" },
            { name: "Logistics", path: "/logistics" },
            { name: "Inventory", path: "/inventory" }
          ]
        }]
      : []),
    ...(canAccessBD
      ? [{ 
          name: "Business Development", 
          icon: TrendingUp,
          subItems: [
            { name: "Opportunities", path: "/opportunities" },
            { name: "Marketing", path: "/marketing" },
            { name: "Partners", path: "/partners" },
            { name: "Weekly Status", path: "/weekly-status" },
            { name: "Meetings", path: "/meetings" }
          ]
        }]
      : []),
    ...(canAccessCRM
      ? [{
          name: "CRM",
          icon: Users,
          subItems: [
            { name: "Customers", path: "/customers" },
            { name: "Vendors", path: "/vendors" }
          ]
        }]
      : []),
    ...(canAccessManagement
      ? [{
          name: "Management",
          icon: PieChart,
          subItems: [
            { name: "Reports", path: "/reports" },
            { name: "Financials", path: "/financials" },
            ...(userRoles.includes("Developer") || userRoles.includes("Admin") ? [{ name: "Support", path: "/support" }] : [])
          ]
        }]
      : []),
    ...(canAccessAdminTools || canAccessSettings
      ? [{
          name: "Admin",
          icon: Shield,
          subItems: [
            ...(canAccessAdminTools ? [
              { name: "Users", path: "/users" },
              { name: "Compliance Center", path: "/compliance" },
              { name: "Audit Logs", path: "/audit-logs" },
              { name: "Backup & Recovery", path: "/backup-recovery" }
            ] : []),
            ...(canAccessSettings ? [{ name: "Settings", path: "/settings" }] : [])
          ]
        }]
      : [])
  ];




  return (
    <>
      {/* Mobile Top bar */}
      <header className="mobile-header">
        <div className="mobile-logo">
          {companyLogo ? (
            <img src={companyLogo} alt="Logo" style={{ width: "30px", height: "30px", borderRadius: "6px", objectFit: "cover", marginRight: "0.6rem" }} />
          ) : (
            <img src="/favicon.png" alt="Logo" style={{ width: "30px", height: "30px", borderRadius: "6px", objectFit: "cover", marginRight: "0.6rem" }} />
          )}
          <span>Atlas</span>
        </div>
        <button className="mobile-toggle" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Sidebar navigation */}
      <aside ref={sidebarRef} className={`sidebar ${isOpen ? "open" : ""} ${isCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-brand" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <div className="brand-logo-container">
            {companyLogo ? (
              <img src={companyLogo} alt="Logo" className="brand-logo-img" />
            ) : (
              <img src="/favicon.png" alt="Logo" className="brand-logo-img" />
            )}
            <h2 className="brand-title">Atlas</h2>
          </div>
          <button 
            type="button"
            onClick={toggleCollapse}
            className="collapse-toggle-btn"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
          <button 
            type="button"
            onClick={() => setIsOpen(false)}
            className="mobile-sidebar-close-btn"
            aria-label="Close navigation menu"
          >
            <X size={22} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul>
            {navItems.map((item) => {
              const Icon = item.icon;
              
              if (item.subItems) {
                const isActive = item.subItems.some(sub => location.pathname === sub.path);
                const isOpenMenu = openMenus[item.name] !== undefined ? openMenus[item.name] : (isCollapsed ? false : isActive);
                return (
                  <li key={item.name} style={{ position: "relative" }} className="parent-nav-item">
                    <div
                      className={`sidebar-link ${isActive ? "active-parent" : ""}`}
                      onClick={() => toggleMenu(item.name)}
                      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', paddingRight: '0.5rem' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <Icon size={20} className="link-icon" />
                        <span className="link-text">{item.name}</span>
                      </div>
                      <div style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }} className="sidebar-link-chevron">
                        {isOpenMenu ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </div>
                    </div>
                    <ul className={`submenu-list ${isOpenMenu ? "open" : ""}`}>
                      {item.subItems.map(sub => {
                        const isSubActive = location.pathname === sub.path;
                        return (
                          <li key={sub.path} style={{ marginBottom: '0.25rem' }}>
                            <Link
                              to={sub.path}
                              className={`sidebar-link ${isSubActive ? "active" : ""}`}
                              onClick={() => setIsOpen(false)}
                              style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                            >
                              <span className="link-text">{sub.name}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              }

              const isActive = location.pathname === item.path;
              return (
                <li key={item.path || item.name}>
                  <Link
                    to={item.path}
                    className={`sidebar-link ${isActive ? "active" : ""}`}
                    onClick={() => setIsOpen(false)}
                    style={{ position: "relative" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", width: "100%" }}>
                      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                        <Icon 
                          size={20} 
                          className="link-icon" 
                          style={
                            item.name === "Chat" && hasUnreadChat 
                              ? { fill: "red", color: "red" } 
                              : {}
                          } 
                        />
                        {item.name === "Chat" && hasUnreadChat && isCollapsed && (
                          <span style={{ 
                            position: "absolute",
                            top: "-2px",
                            right: "-2px",
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            backgroundColor: "var(--danger)"
                          }} />
                        )}
                      </div>
                      {!isCollapsed && (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flex: 1 }}>
                          <span className="link-text">{item.name}</span>
                          {item.name === "Chat" && hasUnreadChat && (
                            <span style={{ 
                              width: "6px", 
                              height: "6px", 
                              borderRadius: "50%", 
                              backgroundColor: "var(--danger)",
                              marginRight: "0.25rem"
                            }} />
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Notification Bell, Weekly Status Logs & Bug Icon */}
        <div className="sidebar-notif-area" style={{ display: "flex", gap: "0.4rem", width: "100%", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <NotificationBell
              notifications={getVisibleNotifications(userRoles, userName)}
              unreadCount={getUnreadCount(userRoles, userName)}
              onMarkRead={markAsRead}
              onMarkAllRead={() => markAllAsRead(userRoles, userName)}
              onClearAll={() => clearAll(userRoles, userName)}
              currentUserId={currentUser?.uid}
              userRoles={userRoles}
            />
          </div>
          <button 
            className="notif-bell-btn" 
            onClick={() => setWeeklyStatusLogsModalOpen(true)} 
            aria-label="Atlas Control Change Logs"
            title="Atlas Control Change Logs"
            style={{ width: "auto", padding: "0.5rem 0.65rem", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <History size={19} />
          </button>
          <button 
            className="notif-bell-btn" 
            onClick={() => setDeveloperModalOpen(true)} 
            aria-label="Send to Developer"
            title="Report Bug / Send to Developer"
            style={{ width: "auto", padding: "0.5rem 0.65rem", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Bug size={19} />
          </button>
        </div>

        <div className="sidebar-footer" style={{ padding: "1rem 0.5rem" }}>
          {currentUser && (
            <Link 
              to="/profile" 
              className="sidebar-profile-card"
              onClick={() => setIsOpen(false)}
              style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "0.75rem", 
                padding: "0.75rem", 
                borderRadius: "var(--radius-sm)", 
                marginBottom: "0.75rem", 
                textDecoration: "none", 
                color: "var(--text-primary)",
                borderBottom: "1px solid var(--border-color)",
                transition: "var(--transition-normal)",
                background: location.pathname === "/profile" ? "var(--primary-light)" : "transparent"
              }}
            >
              {profilePic ? (
                <img src={profilePic} alt="Avatar" style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
              ) : (
                <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "0.85rem", color: "#ffffff", flexShrink: 0 }}>
                  {userName ? userName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "U"}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }} className="profile-text-container">
                <span className="link-text" style={{ fontSize: "0.9rem", fontWeight: "600", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userName}</span>
                <span className="link-text text-secondary" style={{ fontSize: "0.75rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{currentUser.email}</span>
              </div>
            </Link>
          )}
          <button className="logout-btn" onClick={logout}>
            <LogOut size={20} />
            <span className="link-text">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Overlay to close sidebar on mobile click outside */}
      {isOpen && <div className="sidebar-overlay" onClick={() => setIsOpen(false)} />}

      <style>{`
        /* ── Sidebar Collapse & Base Styles ── */
        .collapse-toggle-btn {
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.25rem;
          border-radius: 4px;
          transition: var(--transition-normal);
        }
        .collapse-toggle-btn:hover {
          color: var(--primary);
          background-color: hsla(var(--hue-primary), 89%, 60%, 0.05);
        }

        .mobile-sidebar-close-btn {
          display: none;
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 0.35rem;
          border-radius: 6px;
          transition: var(--transition-normal);
        }
        .mobile-sidebar-close-btn:hover {
          color: var(--text-primary);
          background-color: rgba(255, 255, 255, 0.08);
        }

        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: var(--sidebar-width);
          background-color: var(--bg-sidebar);
          border-right: 1px solid var(--border-color);
          display: flex;
          flex-direction: column;
          z-index: 99;
          transition: var(--transition-normal);
        }

        .sidebar-brand {
          height: var(--header-height);
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0 1.25rem;
          border-bottom: 1px solid var(--border-color);
        }

        .brand-logo-container {
          display: flex;
          align-items: center;
        }

        .brand-logo-img {
          width: 36px;
          height: 36px;
          border-radius: 6px;
          object-fit: cover;
          margin-right: 0.65rem;
          transition: margin var(--transition-normal), width var(--transition-normal), height var(--transition-normal);
        }

        .sidebar-brand h2 {
          font-size: 1.15rem;
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff 50%, var(--text-secondary) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .sidebar-nav {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 1.25rem 0.75rem;
          scrollbar-width: thin;
          scrollbar-color: var(--border-color) transparent;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 5px;
        }
        .sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-nav::-webkit-scrollbar-thumb {
          background: var(--border-color);
          border-radius: 4px;
        }
        .sidebar-nav::-webkit-scrollbar-thumb:hover {
          background: var(--text-muted);
        }

        .sidebar-nav ul {
          list-style: none;
        }
        .sidebar-nav li {
          margin-bottom: 0.4rem;
        }

        .submenu-list {
          display: none;
          list-style: none;
          padding-left: 2.5rem;
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
        }
        .submenu-list.open {
          display: block;
        }

        .sidebar-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.85rem 1rem;
          border-radius: var(--radius-sm);
          color: var(--text-secondary);
          font-weight: 500;
          font-size: 0.95rem;
          transition: var(--transition-normal);
          position: relative;
        }
        .sidebar-link:hover {
          color: var(--text-primary);
          background-color: hsla(var(--hue-primary), 89%, 60%, 0.05);
        }
        .sidebar-link.active {
          color: var(--text-primary);
          background: linear-gradient(90deg, var(--primary-light) 0%, transparent 100%);
          border-left: 3px solid var(--primary);
          padding-left: calc(1rem - 3px);
        }
        .sidebar-link.active .link-icon {
          color: var(--primary);
        }
        .link-icon {
          transition: var(--transition-normal);
        }
        .sidebar-link:hover .link-icon {
          transform: translateX(2px);
          color: var(--primary);
        }

        .sidebar-footer {
          padding: 1rem 0.75rem;
          border-top: 1px solid var(--border-color);
        }

        .logout-btn {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          padding: 0.85rem 1rem;
          border-radius: var(--radius-sm);
          background: none;
          border: none;
          color: var(--text-muted);
          font-weight: 500;
          font-size: 0.95rem;
          cursor: pointer;
          transition: var(--transition-normal);
        }
        .logout-btn:hover {
          color: var(--danger);
          background-color: var(--danger-light);
        }

        .mobile-header {
          display: none;
        }
        .sidebar-overlay {
          display: none;
        }

        /* ── Desktop View (≥ 1024px) ── */
        @media (min-width: 1024px) {
          .mobile-header {
            display: none !important;
          }
          .mobile-sidebar-close-btn {
            display: none !important;
          }
          .sidebar-overlay {
            display: none !important;
          }

          /* Collapsed Sidebar on Desktop */
          body.sidebar-collapsed .sidebar {
            width: 80px;
          }
          body.sidebar-collapsed .brand-logo-img {
            margin-right: 0 !important;
          }
          body.sidebar-collapsed .brand-title,
          body.sidebar-collapsed .link-text,
          body.sidebar-collapsed .sidebar-link-chevron {
            display: none !important;
          }
          body.sidebar-collapsed .sidebar-brand {
            justify-content: center !important;
            padding: 0;
          }
          body.sidebar-collapsed .sidebar-link {
            justify-content: center;
            padding: 1rem;
          }
          body.sidebar-collapsed .logout-btn {
            justify-content: center;
            padding: 1rem;
          }
          body.sidebar-collapsed .sidebar-profile-card {
            justify-content: center;
            padding: 0.75rem 0 !important;
            border-bottom: none !important;
          }
          body.sidebar-collapsed .sidebar-notif-area {
            flex-direction: column;
            align-items: center;
            gap: 0.75rem;
            padding: 0.5rem 0;
          }
          body.sidebar-collapsed .main-content {
            margin-left: 80px;
          }
          
          /* Collapsed Sidebar Flyout Submenu (Triggered by click) */
          body.sidebar-collapsed .submenu-list {
            display: none !important;
          }
          body.sidebar-collapsed .submenu-list.open {
            display: block !important;
            position: absolute;
            left: 70px;
            top: 0;
            background-color: var(--bg-sidebar);
            border: 1px solid var(--border-color);
            border-radius: var(--radius-sm);
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
            width: 180px;
            padding: 0.5rem;
            z-index: 1000;
            margin: 0;
            list-style: none;
          }
          body.sidebar-collapsed .submenu-list.open li {
            margin-bottom: 0.25rem;
          }
          body.sidebar-collapsed .submenu-list.open .sidebar-link {
            justify-content: flex-start !important;
            padding: 0.5rem 1rem !important;
            font-size: 0.9rem !important;
            background: none !important;
            border-left: none !important;
          }
          body.sidebar-collapsed .submenu-list.open .sidebar-link.active {
            color: var(--primary) !important;
            font-weight: 700;
          }
          body.sidebar-collapsed .submenu-list.open .link-text {
            display: inline !important;
            color: var(--text-secondary);
          }
          body.sidebar-collapsed .submenu-list.open .sidebar-link:hover .link-text {
            color: var(--text-primary);
          }
        }

        /* ── Mobile & Tablet View (< 1024px) ── */
        @media (max-width: 1023px) {
          body.mobile-nav-open {
            overflow: hidden;
          }

          .collapse-toggle-btn {
            display: none !important;
          }

          .mobile-sidebar-close-btn {
            display: flex !important;
          }

          .mobile-header {
            display: flex !important;
            justify-content: space-between;
            align-items: center;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            height: var(--header-height);
            background-color: var(--bg-sidebar);
            border-bottom: 1px solid var(--border-color);
            padding: 0 1.25rem;
            z-index: 1000;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
          }

          .mobile-logo {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-family: var(--font-heading);
            font-weight: 700;
            font-size: 1.15rem;
            color: var(--text-primary);
          }

          .mobile-toggle {
            background: none;
            border: none;
            color: var(--text-primary);
            cursor: pointer;
            padding: 0.35rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            transition: var(--transition-normal);
          }
          .mobile-toggle:hover {
            background-color: rgba(255, 255, 255, 0.08);
          }

          /* Drawer Sidebar on Mobile & Tablet */
          .sidebar {
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            width: 290px !important;
            max-width: 85vw;
            background-color: var(--bg-sidebar);
            border-right: 1px solid var(--border-color);
            display: flex !important;
            flex-direction: column;
            z-index: 1002;
            transform: translateX(-100%);
            transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
            box-shadow: none;
          }

          .sidebar.open {
            transform: translateX(0);
            box-shadow: 0 0 40px rgba(0, 0, 0, 0.6);
          }

          /* Force all labels, titles, and submenus to be fully visible on mobile */
          .sidebar .sidebar-brand {
            display: flex !important;
            justify-content: space-between !important;
            padding: 0 1.25rem !important;
            height: var(--header-height);
          }

          .sidebar .brand-title,
          .sidebar .link-text {
            display: inline !important;
          }

          .sidebar .sidebar-link-chevron {
            display: flex !important;
          }

          .sidebar .sidebar-link {
            justify-content: flex-start !important;
            padding: 0.85rem 1rem !important;
          }

          .sidebar .submenu-list {
            list-style: none;
            padding-left: 2.25rem !important;
            margin: 0.25rem 0 !important;
          }

          .sidebar .submenu-list.open {
            display: block !important;
            position: static !important;
            width: auto !important;
            box-shadow: none !important;
            border: none !important;
            background: transparent !important;
          }

          .sidebar .submenu-list .sidebar-link {
            padding: 0.6rem 0.75rem !important;
            font-size: 0.9rem !important;
          }

          .sidebar .submenu-list .link-text {
            display: inline !important;
            color: var(--text-secondary);
          }

          .sidebar .submenu-list .sidebar-link.active .link-text {
            color: var(--primary) !important;
          }

          .sidebar .logout-btn {
            justify-content: flex-start !important;
            padding: 0.85rem 1rem !important;
          }

          .sidebar-overlay {
            display: block;
            position: fixed;
            inset: 0;
            background-color: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(3px);
            z-index: 1001;
            animation: fadeIn 0.2s ease;
          }
        }
      `}</style>
      <SendToDeveloperModal 
        isOpen={developerModalOpen} 
        onClose={() => setDeveloperModalOpen(false)} 
        userName={userName} 
      />
      {weeklyStatusLogsModalOpen && (
        <WeeklyStatusLogsModal
          isOpen={weeklyStatusLogsModalOpen}
          onClose={() => setWeeklyStatusLogsModalOpen(false)}
        />
      )}
    </>
  );
}
